<template>
  <div class="space-y-4">
    <!-- Format -->
    <div>
      <label class="text-sm font-medium text-gray-800 mb-1 block">Format</label>
      <select
        v-model="form.format"
        class="w-full text-sm text-gray-900 border border-gray-300 rounded px-2 py-1.5 bg-white focus:outline-none focus:border-teal-500"
      >
        <optgroup v-for="group in formatGroups" :key="group.label" :label="group.label">
          <option v-for="f in group.formats" :key="f" :value="f">{{ formatLabel(f) }}</option>
        </optgroup>
      </select>
    </div>

    <!-- Brief -->
    <div>
      <label class="text-sm font-medium text-gray-800 mb-1 block">Brief</label>
      <textarea
        v-model="form.brief"
        rows="12"
        :placeholder="briefPlaceholder"
        class="w-full text-sm text-gray-900 border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-teal-500 resize-y"
      ></textarea>
    </div>

    <!-- Title (optional) -->
    <div>
      <label class="text-sm font-medium text-gray-800 mb-1 block">
        Title
        <span class="font-normal text-gray-500">(optional)</span>
      </label>
      <input
        v-model="form.title"
        type="text"
        placeholder="Leave blank to let the AI generate one, or set your own"
        class="w-full text-sm text-gray-900 border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-teal-500"
      />
    </div>

    <!-- Model selector -->
    <div>
      <label class="text-sm font-medium text-gray-800 mb-1 block">Model</label>
      <select
        :value="store.selectedModel.value"
        @change="store.setModel($event.target.value)"
        class="w-full text-sm text-gray-900 border border-gray-300 rounded px-2 py-1.5 bg-white focus:outline-none focus:border-teal-500"
      >
        <option v-for="m in store.availableModels" :key="m.key" :value="m.key">{{ m.label }}</option>
      </select>
    </div>

    <!-- Generate / Stop button -->
    <div class="pt-2 border-t border-gray-200">
      <div
        v-if="store.generationError.value"
        class="bg-red-50 border border-red-200 text-red-800 text-sm px-3 py-2 rounded mb-2"
      >
        {{ store.generationError.value }}
        <button @click="store.generationError.value = null" class="ml-2 text-red-600 font-bold">&times;</button>
      </div>

      <div
        v-if="!canGenerate && !store.generating.value && missingHint"
        class="text-sm text-gray-500 mb-2"
      >
        {{ missingHint }}
      </div>

      <button
        v-if="store.generating.value"
        @click="store.stopGeneration()"
        class="w-full flex items-center justify-center gap-2 bg-red-600 text-white px-4 py-2.5 rounded hover:bg-red-700 transition-colors text-sm font-medium"
      >
        <Square :size="14" />
        Stop
      </button>
      <div v-else class="flex gap-2">
        <button
          v-if="!store.currentContent.value && canGenerate"
          @click="handleSaveDraft"
          :disabled="store.loading.value"
          class="flex items-center justify-center gap-2 bg-gray-200 text-gray-800 px-4 py-2.5 rounded hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
        >
          <Save :size="14" />
          Save draft
        </button>
        <button
          @click="handleGenerate"
          :disabled="store.loading.value || !canGenerate"
          class="flex-1 flex items-center justify-center gap-2 bg-teal-600 text-white px-4 py-2.5 rounded hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
        >
          <Play :size="14" />
          {{ hasBody ? 'Rewrite' : 'Generate' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { reactive, ref, watch, computed, inject } from 'vue'
import { Play, Square, Save } from 'lucide-vue-next'

const store = inject('workbench')

const formatGroups = [
  { label: 'General', formats: ['article', 'personal_essay', 'storytelling'] },
  { label: 'Opinion & Commentary', formats: ['editorial', 'op_ed', 'column', 'commentary', 'review_critique'] },
  { label: 'Features & Long-form', formats: ['feature_article', 'narrative', 'deep_dive', 'case_study'] },
  { label: 'Short-form & Quick Hits', formats: ['brief_post', 'listicle', 'faq', 'explainer', 'how_to'] },
  { label: 'Blog & Recurring', formats: ['blog_post', 'guest_post', 'roundup', 'newsletter', 'digest'] },
  { label: 'Social Media', formats: ['social_post', 'linkedin_post', 'twitter_thread', 'instagram_caption'] },
  { label: 'Other', formats: ['email'] },
]

const briefPlaceholder = `Describe what you want to write. Your angle, key points, thesis \u2014 even rough notes work.\n\nExample:\nMany WordPress agencies hold hosting hostage. I want an article about why migrating your site isn\u2019t as scary as it seems.\n\n- Key points: vendor lock-in is a myth, migration takes hours not weeks\n- Tone: direct, confident, slightly provocative\n- Target: small business owners with a WP site`

function formatLabel(f) {
  return f.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
}

const form = reactive({
  brief: '',
  title: '',
  format: 'article',
})

// Sync form when content loads
watch(() => store.currentContent.value, (content) => {
  if (content) {
    form.brief = content.brief || ''
    form.title = content.title || ''
    form.format = content.format || 'article'
  }
}, { immediate: true })

// Sync to store reactively
store.inputForm.value = form

watch(form, () => {
  store.inputForm.value = { ...form }
}, { deep: true })

// --- Generate button logic ---
const hasBrief = computed(() => store.inputForm.value?.brief?.trim())
const hasAuthor = computed(() => !!store.currentAuthor.value)
const canGenerate = computed(() => hasBrief.value && hasAuthor.value)
const hasBody = computed(() => !!store.currentContent.value?.body)

const missingHint = computed(() => {
  const missing = []
  if (!hasAuthor.value) missing.push('select an author')
  if (!hasBrief.value) missing.push('enter a brief')
  if (!missing.length) return null
  return 'To generate: ' + missing.join(' and ')
})

async function handleSaveDraft() {
  if (!canGenerate.value) return

  const contentData = {
    brief: form.brief,
    title: form.title || null,
    format: form.format || 'article',
    status: 'draft',
    author_id: store.currentAuthor.value.id,
  }

  await store.saveContent(contentData)
}

async function handleGenerate() {
  if (!canGenerate.value) return

  const contentData = {
    brief: form.brief,
    title: form.title || null,
    format: form.format || 'article',
    status: 'draft',
    author_id: store.currentAuthor.value.id,
  }

  await store.saveContent(contentData)
  await store.generate({ rewrite: hasBody.value })
}
</script>
